(function () {
  if (customElements.get('bloom-video-gallery')) return;

  const RELEASE_ENDPOINT = 'https://eg2o7r7cs9.execute-api.us-west-2.amazonaws.com/prod/api/v2/journeys/';

  class BloomVideoGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-video-viewport]');
      this.track = this.querySelector('[data-video-track]');
      this.fallback = this.querySelector('[data-video-fallback]');
      this.carouselPrevious = this.querySelector('[data-carousel-previous]');
      this.carouselNext = this.querySelector('[data-carousel-next]');
      this.modal = this.querySelector('[data-video-modal]');
      this.modalStage = this.querySelector('[data-modal-stage]');
      this.modalStatus = this.querySelector('[data-modal-status]');
      this.modalClose = this.querySelector('[data-modal-close]');
      this.modalPrevious = this.querySelector('[data-modal-previous]');
      this.modalNext = this.querySelector('[data-modal-next]');
      this.journeyId = (this.dataset.journeyId || '').trim();
      this.rootMargin = Math.max(0, Number.parseInt(this.dataset.rootMargin || '300', 10) || 300);
      this.abortController = new AbortController();
      this.activeIndex = 0;
      this.returnFocus = null;

      if (!this.viewport || !this.track || !this.modal || !/^[A-Za-z0-9_-]+$/.test(this.journeyId)) {
        this.showFallback();
        return;
      }

      this.bindEvents();
      this.prepareGallery();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.abortController?.abort();
      this.closeModal(false);
      window.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('resize', this.updateCarouselControls);
    }

    bindEvents() {
      this.handleTrackClick = (event) => {
        const trigger = event.target.closest('[data-video-index]');
        if (!trigger || !this.contains(trigger)) return;
        this.openModal(Number.parseInt(trigger.dataset.videoIndex, 10), trigger);
      };

      this.handleKeydown = (event) => {
        if (this.modal.hidden) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeModal();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.changeVideo(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.changeVideo(1);
        }
      };

      this.handleModalTouchStart = (event) => {
        if (event.touches.length !== 1) {
          this.modalTouchStart = null;
          return;
        }

        this.modalTouchStart = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY
        };
      };

      this.handleModalTouchEnd = (event) => {
        if (!this.modalTouchStart || event.changedTouches.length !== 1 || this.modal.hidden) {
          this.modalTouchStart = null;
          return;
        }

        const deltaX = event.changedTouches[0].clientX - this.modalTouchStart.x;
        const deltaY = event.changedTouches[0].clientY - this.modalTouchStart.y;
        this.modalTouchStart = null;

        if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
        this.changeVideo(deltaX < 0 ? 1 : -1);
      };

      this.updateCarouselControls = () => {
        if (!this.track || !this.carouselPrevious || !this.carouselNext) return;
        const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const hasOverflow = maximumScroll > 2;
        const hideDirectionalControls = window.matchMedia('(max-width: 749px)').matches;

        this.carouselPrevious.hidden = hideDirectionalControls || !hasOverflow;
        this.carouselNext.hidden = hideDirectionalControls || !hasOverflow;
        this.carouselPrevious.disabled = hideDirectionalControls || !hasOverflow || this.track.scrollLeft <= 2;
        this.carouselNext.disabled = hideDirectionalControls || !hasOverflow || this.track.scrollLeft >= maximumScroll - 2;

        const showModalControls = !hideDirectionalControls && (this.videos?.length || 0) > 1;
        if (this.modalPrevious) this.modalPrevious.hidden = !showModalControls;
        if (this.modalNext) this.modalNext.hidden = !showModalControls;
      };

      this.track.addEventListener('click', this.handleTrackClick);
      this.track.addEventListener('scroll', this.updateCarouselControls, { passive: true });
      this.carouselPrevious?.addEventListener('click', () => this.scrollGallery(-1));
      this.carouselNext?.addEventListener('click', () => this.scrollGallery(1));
      this.modalClose?.addEventListener('click', () => this.closeModal());
      this.modalPrevious?.addEventListener('click', () => this.changeVideo(-1));
      this.modalNext?.addEventListener('click', () => this.changeVideo(1));
      this.modalStage?.addEventListener('touchstart', this.handleModalTouchStart, { passive: true });
      this.modalStage?.addEventListener('touchend', this.handleModalTouchEnd, { passive: true });
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });
      window.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('resize', this.updateCarouselControls, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateCarouselControls);
        this.resizeObserver.observe(this.track);
      }
    }

    prepareGallery() {
      if (!('IntersectionObserver' in window)) {
        this.loadGallery();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer?.disconnect();
        this.observer = null;
        this.loadGallery();
      }, { rootMargin: `${this.rootMargin}px 0px`, threshold: 0 });

      this.observer.observe(this);
    }

    async loadGallery() {
      if (this.loading || this.loaded) return;
      this.loading = true;

      try {
        const response = await fetch(
          `${RELEASE_ENDPOINT}${encodeURIComponent(this.journeyId)}/releases?type=PUBLISH&sort=createdAt&order=DESC&size=1`,
          { signal: this.abortController.signal }
        );

        if (!response.ok) throw new Error(`Video release request failed: ${response.status}`);

        const payload = await response.json();
        const release = this.findPublishedRelease(payload);
        const videos = this.getOrderedVideos(release);

        if (!release?.isPublished || !videos.length) throw new Error('No published videos were found');

        this.videos = videos;
        this.renderGallery();
        this.loaded = true;
      } catch (error) {
        if (error.name !== 'AbortError') this.showFallback();
      } finally {
        this.loading = false;
      }
    }

    findPublishedRelease(payload) {
      const candidates = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.content)
          ? payload.content
          : Array.isArray(payload?.data)
            ? payload.data
            : payload && typeof payload === 'object'
              ? [payload]
              : [];

      return candidates.find((release) => release?.isPublished) || candidates[0] || null;
    }

    getOrderedVideos(release) {
      const manifest = release?.manifestData;
      const contentBlocks = Array.isArray(manifest?.contentBlocks) ? manifest.contentBlocks : [];
      const scenes = Array.isArray(manifest?.scenes) ? manifest.scenes : [];
      const videoBlocks = new Map();

      contentBlocks.forEach((block) => {
        const video = this.normalizeVideo(block);
        if (video) videoBlocks.set(video.id, video);
      });

      const ordered = [];
      const included = new Set();

      scenes.forEach((scene) => {
        const requiredIds = Array.isArray(scene?.requiredContentBlocks) ? scene.requiredContentBlocks : [];
        const videoId = requiredIds.find((id) => videoBlocks.has(id) && !included.has(id));
        if (!videoId) return;
        ordered.push(videoBlocks.get(videoId));
        included.add(videoId);
      });

      videoBlocks.forEach((video, id) => {
        if (included.has(id)) return;
        ordered.push(video);
      });

      return ordered;
    }

    normalizeVideo(block) {
      if (!block?.id || !Array.isArray(block.sources) || !block.sources.length) return null;

      const sources = block.sources
        .map((source) => {
          const src = this.safeUrl(source?.src);
          if (!src) return null;
          return {
            src,
            type: this.getVideoType(source, src),
            priority: this.getSourcePriority(src)
          };
        })
        .filter(Boolean)
        .sort((left, right) => left.priority - right.priority)
        .filter((source, index, list) => list.findIndex((item) => item.src === source.src) === index);

      if (!sources.length) return null;

      return {
        id: String(block.id),
        poster: this.safeUrl(block.videoThumbnail),
        duration: Number(block.videoLength) || 0,
        sources
      };
    }

    safeUrl(value) {
      if (typeof value !== 'string' || !value) return '';
      try {
        const url = new URL(value, window.location.origin);
        return url.protocol === 'https:' ? url.href : '';
      } catch (_error) {
        return '';
      }
    }

    getVideoType(source, src) {
      if (typeof source?.type === 'string' && source.type.startsWith('video/')) return source.type;
      if (/webm/i.test(src)) return 'video/webm';
      return 'video/mp4';
    }

    getSourcePriority(src) {
      if (/vc_h264/i.test(src)) return 0;
      if (/webm|vc_vp9/i.test(src)) return 1;
      if (/mp4/i.test(src)) return 2;
      return 3;
    }

    renderGallery() {
      const fragment = document.createDocumentFragment();

      this.videos.forEach((video, index) => {
        const item = document.createElement('li');
        item.className = 'bloom-multi-video__item';

        const button = document.createElement('button');
        button.className = 'bloom-multi-video__card';
        button.type = 'button';
        button.dataset.videoIndex = String(index);
        button.setAttribute('aria-label', `${this.dataset.playLabel || 'Play video'} ${index + 1}`);

        if (video.poster) {
          const image = document.createElement('img');
          image.className = 'bloom-multi-video__poster';
          image.src = video.poster;
          image.alt = '';
          image.loading = index < 2 ? 'eager' : 'lazy';
          image.decoding = 'async';
          button.appendChild(image);
        }

        const play = document.createElement('span');
        play.className = 'bloom-multi-video__play';
        play.setAttribute('aria-hidden', 'true');
        play.innerHTML = '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"></path></svg>';
        button.appendChild(play);

        item.appendChild(button);
        fragment.appendChild(item);
      });

      this.track.replaceChildren(fragment);
      this.track.setAttribute('aria-busy', 'false');
      this.dataset.ready = 'true';
      requestAnimationFrame(this.updateCarouselControls);
    }

    scrollGallery(direction) {
      const firstItem = this.track.querySelector('.bloom-multi-video__item');
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const distance = firstItem ? firstItem.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: direction * distance, behavior: 'smooth' });
    }

    openModal(index, trigger) {
      if (!this.videos?.[index]) return;
      this.activeIndex = index;
      this.returnFocus = trigger || document.activeElement;
      this.modal.hidden = false;
      document.body.classList.add('bloom-video-modal-open');
      this.renderActiveVideo();
      requestAnimationFrame(() => this.modalClose?.focus());
    }

    renderActiveVideo() {
      const videoData = this.videos?.[this.activeIndex];
      if (!videoData || !this.modalStage) return;

      const currentVideo = this.modalStage.querySelector('video');
      if (currentVideo) currentVideo.pause();

      const video = document.createElement('video');
      video.className = 'bloom-multi-video__modal-video';
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.setAttribute('controlslist', 'nodownload');
      video.setAttribute('aria-label', `Video ${this.activeIndex + 1} of ${this.videos.length}`);
      if (videoData.poster) video.poster = videoData.poster;

      videoData.sources.forEach((sourceData) => {
        const source = document.createElement('source');
        source.src = sourceData.src;
        source.type = sourceData.type;
        video.appendChild(source);
      });

      this.modalStage.replaceChildren(video);
      this.modalStatus.textContent = `${this.activeIndex + 1} / ${this.videos.length}`;
      const showModalControls = this.videos.length > 1 && !window.matchMedia('(max-width: 749px)').matches;
      this.modalPrevious.hidden = !showModalControls;
      this.modalNext.hidden = !showModalControls;
      video.play().catch(() => {});
    }

    changeVideo(direction) {
      if (!this.videos?.length) return;
      this.activeIndex = (this.activeIndex + direction + this.videos.length) % this.videos.length;
      this.renderActiveVideo();

      const card = this.track.querySelector(`[data-video-index="${this.activeIndex}"]`);
      card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    closeModal(restoreFocus = true) {
      if (!this.modal || this.modal.hidden) return;
      const video = this.modalStage?.querySelector('video');
      if (video) video.pause();
      this.modalStage?.replaceChildren();
      this.modal.hidden = true;
      document.body.classList.remove('bloom-video-modal-open');

      if (restoreFocus && this.returnFocus instanceof HTMLElement) this.returnFocus.focus();
      this.returnFocus = null;
    }

    showFallback() {
      if (this.track) {
        this.track.replaceChildren();
        this.track.setAttribute('aria-busy', 'false');
      }
      if (this.viewport) this.viewport.hidden = true;
      if (this.fallback) this.fallback.hidden = false;
    }
  }

  customElements.define('bloom-video-gallery', BloomVideoGallery);
})();
